import React, { useEffect, useState } from "react";
import { ImageOff } from "lucide-react";

import { loadPresignedImageObjectUrl } from "@/utils/presignedImageCache";

// An evidence screenshot, with one-shot recovery from an expired link.
//
// `render` and `additionalRenders[].url` are not stored URLs. AppSync presigns
// the underlying S3 key at QUERY time (see the backend's presign_url_handler),
// and that signature is only valid for PRESIGNED_URL_EXPIRY_SECONDS -- 900s /
// 15 minutes, in the backend's models/definitions.py. The clock therefore starts
// when the finding is opened, not when the user logs in: leave a finding on
// screen for a quarter of an hour and its screenshots stop loading, even though
// the Cognito session is still being refreshed happily in the background.
//
// S3 answers an expired link with 403, and the browser turns that into a bare
// `error` event that is indistinguishable from the object having been deleted.
// So the first failure for a given URL is treated as "probably expired": ask the
// page to re-run its query, which mints a fresh signature, and let the new src
// retry the load. Only when a freshly minted URL ALSO fails do we conclude the
// evidence is genuinely gone and show the empty state.
//
// The retry is capped at one refresh per page view, a budget the page owns via
// `canRequestFreshUrl`. Retrying unconditionally would loop forever on a
// genuinely missing object -- re-querying on every error, which is the same
// failure the avatar fallback used to have.
//
// PREFERRED PATH. Before any of that, the screenshot is fetched once into an
// in-memory blob (see utils/presignedImageCache) and rendered from an object URL.
// The picture then has nothing to do with the signature that delivered it and
// survives however long the finding stays open, so the expiry retry below never
// has to fire. That fetch needs GET in the bucket's CORS rule; where it is not
// yet deployed -- or the object is simply gone -- this falls back to pointing
// <img> straight at the presigned URL, which is what it always did, because
// image elements are not subject to CORS. Deploying this ahead of the CORS
// change is therefore safe: it degrades to the previous behaviour.

const FALLBACK_STYLES = {
  large: { container: "py-12", icon: "h-8 w-8", label: "text-sm" },
  small: { container: "py-8", icon: "h-6 w-6", label: "text-xs" },
} as const;

type EvidenceImageProps = {
  src: string;
  alt: string;
  className: string;
  // Controls only the size of the "failed to load" placeholder, so a thumbnail
  // in the additional-context grid does not get the full-width treatment.
  size: keyof typeof FALLBACK_STYLES;
  // False once the page has spent its single refresh, at which point a failure
  // is final rather than worth another round-trip.
  canRequestFreshUrl: boolean;
  onFreshUrlRequested: () => void;
  // Incremented by the page when a refresh finishes, whatever the outcome. Lets
  // this image notice a refresh that did not actually change its src.
  freshUrlAttemptCount: number;
};

export default function EvidenceImage({
  src,
  alt,
  className,
  size,
  canRequestFreshUrl,
  onFreshUrlRequested,
  freshUrlAttemptCount,
}: EvidenceImageProps) {
  // Both track a specific URL rather than a bare boolean: the page swaps in a
  // newly signed src on refresh, and that new URL deserves its own verdict.
  const [requestedFreshUrlForSrc, setRequestedFreshUrlForSrc] = useState<string | null>(null);
  const [permanentlyFailedSrc, setPermanentlyFailedSrc] = useState<string | null>(null);
  // The cached blob, once held. Null while the fetch is in flight or after it
  // failed, in which case we render the presigned URL directly instead.
  const [cachedObjectUrl, setCachedObjectUrl] = useState<string | null>(null);
  const [isCachingEvidence, setIsCachingEvidence] = useState<boolean>(true);

  useEffect(() => {
    let hasUnmounted = false;
    setCachedObjectUrl(null);
    setIsCachingEvidence(true);

    loadPresignedImageObjectUrl(src)
      .then((objectUrl) => {
        if (!hasUnmounted) setCachedObjectUrl(objectUrl);
      })
      .catch(() => {
        // Expected wherever CORS GET is not deployed. Not logged: it would fire
        // for every screenshot on the page and says nothing the fallback does
        // not already handle.
      })
      .finally(() => {
        if (!hasUnmounted) setIsCachingEvidence(false);
      });

    return () => {
      hasUnmounted = true;
    };
  }, [src]);

  // A refresh that came back without changing this src cannot fix anything --
  // no new request is made, so no further error will ever fire and the image
  // would sit broken with no explanation. Settle it here instead.
  useEffect(() => {
    if (freshUrlAttemptCount > 0 && requestedFreshUrlForSrc === src) {
      setPermanentlyFailedSrc(src);
    }
    // Deliberately keyed on the attempt counter alone: this must run when a
    // refresh completes, not on every src change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshUrlAttemptCount]);

  function handleImageLoadError(): void {
    if (permanentlyFailedSrc === src) return;
    if (canRequestFreshUrl && requestedFreshUrlForSrc !== src) {
      setRequestedFreshUrlForSrc(src);
      onFreshUrlRequested();
      return;
    }
    setPermanentlyFailedSrc(src);
  }

  if (permanentlyFailedSrc === src) {
    const fallbackStyle = FALLBACK_STYLES[size];
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 text-gray-400 bg-gray-50 ${fallbackStyle.container}`}
      >
        <ImageOff className={fallbackStyle.icon} />
        <p className={fallbackStyle.label}>Screenshot failed to load</p>
      </div>
    );
  }

  // Served from the blob: immune to the signature expiring, so no error handler
  // is needed — the bytes are already here.
  if (cachedObjectUrl) {
    return <img src={cachedObjectUrl} alt={alt} className={className} />;
  }

  // Hold the space while the bytes are on their way rather than flashing the
  // presigned URL and then swapping the src, which would fetch the same image
  // twice.
  if (isCachingEvidence) {
    return <div className={`${className} bg-gray-100 animate-pulse`} aria-hidden="true" />;
  }

  return <img src={src} alt={alt} className={className} onError={handleImageLoadError} />;
}
